// Writer único de UNA cuota de plan_cobros_facturas / plan_pagos_facturas (Fase 2 de "Futuros:
// reagendar", Commit 2, 2026-09-24; endurecido en Fase 2b, 2026-09-24; CUTOVER a la RPC atómica en
// Fase 2c, Commit 1, 2026-09-24; CUTOVER a la sobrecarga v2 de la RPC —`p_pais` en vez de
// `p_hoy_pais`, "hoy" resuelto SERVER-SIDE— en Fase 2e, 2026-09-24). Mueve SOLO `fecha_vencimiento`
// de la cuota puntual — nunca renumera `numero_cuota` ni toca las cuotas hermanas del mismo
// plan/factura (editar el plan completo sigue siendo exclusivo del kebab → PlanCuotasModal). Se
// PERMITE que la cuota quede cronológicamente después de la siguiente (decisión de producto
// explícita — a diferencia del Lote CapEx /
// `guardarFechaLoteCapex.js`, que sí valida orden FOB ≤ Transporte < Aduana; ese es un caso distinto,
// no se replica acá).
//
// CUTOVER (Fase 2c): hasta acá este writer hacía, desde JS, (1) releer la cuota real, (2) consultar el
// libro `aplicaciones_saldo`, (3) un UPDATE condicional (`.eq("estado","pendiente")
// .is("movimiento_bancario_id",null)`) — una red mínima contra la carrera, pero sin candado de fila
// real (JS nunca puede dar un `SELECT ... FOR UPDATE`). Ahora, con la RPC `reagendar_cuota_factura`
// aplicada en producción (migración `supabase/migrations/20260924_rpc_reagendar_cuota_factura.sql`,
// candado de fila real dentro de una transacción), este writer se reduce a:
//
//   0. Validar los parámetros básicos en JS (mismos mensajes que antes) + el cross-check
//      `cuotaTablaOrigen` vs `cuota_tabla` (Fase 2b — defensa de UI sobre datos de UI, no necesita la
//      RPC: el caller, `reagendarEntryFuturo`, ya no confía en `f.cuota_tabla` como fuente de la tabla
//      real — la fija por el tipo de entry y manda ese valor como `cuota_tabla`; `f.cuota_tabla` viaja
//      aparte como `cuotaTablaOrigen`, solo para esta verificación cruzada). Fase 2e: se suma acá la
//      validación de `pais` ∈ {"Chile","Perú"} (10.53, mismo formato que ya usa `ctxReagendarFuturo`
//      de FlujoCajaV2) — la RPC v2 lo necesita para resolver "hoy" ella misma, server-side; un valor
//      fuera de ese par también lo rechazaría del lado de la RPC (`RAISE EXCEPTION 'pais desconocido
//      "%"'`), pero se prefiere frenar acá, ANTES de la red, con el mismo estilo que el resto de las
//      validaciones de este archivo (10.177: rechazo explícito, nunca un valor que viaja "a ver si
//      pasa").
//
//   1. Llamar `client.rpc("reagendar_cuota_factura", {...})` con los 4 parámetros de la sobrecarga v2
//      (`p_tabla`, `p_cuota_id`, `p_nueva_fecha`, `p_pais`) — Fase 2e: reemplaza `p_hoy_pais`, que este
//      writer calculaba en JS vía `hoyDelPais(pais)` (`fechasFuturosPanel.js`); ahora la RPC resuelve
//      "hoy" ELLA MISMA, server-side, por huso de país (ver
//      `supabase/migrations/20260924b_rpc_reagendar_cuota_factura_v2.sql`) — este writer ya no calcula
//      ni conoce ese valor. TODAS las validaciones que antes vivían en JS (estado pendiente, sin
//      movimiento bancario, documento_tipo real ≠ factura_externa, sin eventos en el libro, piso de
//      fecha, día hábil) siguen viviendo DENTRO de la RPC, dentro de la MISMA transacción que el
//      `SELECT ... FOR UPDATE` — 10.1: no se duplican acá "por las dudas" (eso mantendría dos criterios
//      que podrían divergir, exactamente lo que la Fase 2b ya cerró de otra forma para el predicado de
//      pantalla).
//
//   2. Si la RPC devuelve `{error}`: el mensaje real de Postgres llega con el prefijo
//      `"reagendar_cuota_factura: "` (ver la RPC, RAISE EXCEPTION) — se limpia ese prefijo para que el
//      toast que ve Ignacio sea legible, sin el nombre técnico de la función por delante. Un error SIN
//      ese prefijo (caída de red, 42501/42883, cualquier error inesperado de Postgres) se propaga TAL
//      CUAL — nunca se esconde detrás de un mensaje genérico (10.177). Fase 2d (2026-09-24, cierre de
//      fisuras de la auditoría de 2b+2c, fisura f): un `{error}` cuyo `.message` NO es un string no
//      vacío (edge case real de ciertos errores de red/Postgres) NUNCA se trata como si no hubiera
//      error ni se devuelve un mensaje vacío/confuso — cae a
//      `"Error sin mensaje de la RPC (code ${error.code ?? "desconocido"})"`. Un `client.rpc` que
//      RECHAZA la promesa entera (excepción JS antes de llegar a un `{data,error}` normal de
//      PostgREST — timeout, red caída) tampoco revienta este writer sin control: se captura y se
//      devuelve con el MISMO contrato `{error:string}`, nunca una excepción no manejada.
//
//   3. Si no hay error: devuelve la fila actualizada que trae `data` (la RPC responde jsonb con la fila
//      real de `plan_cobros_facturas`/`plan_pagos_facturas`). `data:null` con `error:null` (ambos nulos
//      a la vez, edge case raro pero posible) se trata como ÉXITO con `cuota:null` — `{error:null}` es
//      la señal autoritativa de "no hubo error" en el contrato de `client.rpc`; no se inventa acá un
//      tercer estado "ambiguo".
//
// El predicado de pantalla `esCuotaReagendable`/`cuotaReagendable.js` (qué fila es CLICKEABLE) NO
// CAMBIA — sigue siendo el gate de UI que decide qué se muestra como reagendable; la RPC es ahora la
// garantía real detrás de ese click, no el criterio de qué mostrar.
import { client } from "./supabase";

// Lista BLANCA de tablas soportadas — mismo criterio que la RPC (rechaza cualquier otro valor,
// incluido NULL, ANTES de tocar nada).
const TABLAS_SOPORTADAS = new Set(["plan_cobros_facturas", "plan_pagos_facturas"]);

// Lista BLANCA de países soportados (10.53) — Fase 2e: la RPC v2 usa `p_pais` para resolver "hoy"
// server-side por huso; mismo criterio que TABLAS_SOPORTADAS de arriba, rechaza cualquier otro valor
// ANTES de llamar a la RPC (que igual lo rechazaría, pero se prefiere frenar acá — 10.177).
const PAISES_SOPORTADOS = new Set(["Chile", "Perú"]);

// Prefijo que la RPC antepone a TODOS sus mensajes (`RAISE EXCEPTION 'reagendar_cuota_factura: ...'`,
// ver supabase/migrations/20260924_rpc_reagendar_cuota_factura.sql) — se limpia acá, nunca en la RPC
// (10.177: acá se limpia para legibilidad de UI, no se esconde el motivo real).
const PREFIJO_ERROR_RPC = "reagendar_cuota_factura: ";

/**
 * @param {Object} params
 * @param {number} params.cuota_id - `plan_cobros_facturas.id` o `plan_pagos_facturas.id`.
 * @param {"plan_cobros_facturas"|"plan_pagos_facturas"} params.cuota_tabla - tabla AUTORITATIVA,
 *   fijada por el caller según el tipo de entry (nunca un dato de UI sin verificar).
 * @param {"plan_cobros_facturas"|"plan_pagos_facturas"|undefined} [params.cuotaTablaOrigen] -
 *   `cuota_tabla` tal como venía en la entry de UI (10.234/10.243 defensa en profundidad, Fase 2b):
 *   si viene y NO coincide con `cuota_tabla`, se rechaza explícito antes de tocar nada.
 * @param {string} params.nuevaFecha - "YYYY-MM-DD"
 * @param {string} params.pais - "Chile" | "Perú" EXACTO (10.53) — viaja tal cual como `p_pais` a la
 *   RPC, que resuelve "hoy" ELLA MISMA server-side por huso de país (Fase 2e); cualquier otro valor
 *   se rechaza acá mismo, antes de llamar a la RPC.
 * @returns {Promise<{error:string}|{success:true, cuota_id:number, cuota_tabla:string,
 *   nuevaFecha:string, cuota:Object}>}
 */
export const guardarFechaCuotaFactura = async ({ cuota_id, cuota_tabla, cuotaTablaOrigen, nuevaFecha, pais }) => {
  if (!TABLAS_SOPORTADAS.has(cuota_tabla)) {
    return { error: `guardarFechaCuotaFactura: cuota_tabla no soportada "${cuota_tabla}".` };
  }
  if (cuota_id == null) return { error: "guardarFechaCuotaFactura: falta cuota_id." };
  if (!nuevaFecha) return { error: "Seleccioná una fecha." };
  // Cross-check defensivo (Fase 2b): `cuota_tabla` es la fuente autoritativa (la fija el caller por
  // tipo de entry); `cuotaTablaOrigen` es el dato de UI — si vino y no coincide, alguien desincronizó
  // el tipo de entry del dato de la cuota (bug a corregir, no un caso a tragar — 10.177).
  if (cuotaTablaOrigen != null && cuotaTablaOrigen !== cuota_tabla) {
    return {
      error:
        `guardarFechaCuotaFactura: inconsistencia de tabla — el tipo de fila indica "${cuota_tabla}" ` +
        `pero la cuota traía "${cuotaTablaOrigen}".`,
    };
  }
  // Fase 2e: `pais` viaja tal cual como `p_pais` a la RPC v2, que lo usa para resolver "hoy"
  // server-side por huso (10.53) — se valida acá, ANTES de la red, con el mismo criterio de lista
  // blanca que `cuota_tabla` arriba.
  if (!PAISES_SOPORTADOS.has(pais)) {
    return { error: `guardarFechaCuotaFactura: país no soportado "${pais}".` };
  }

  let data;
  let error;
  try {
    ({ data, error } = await client.rpc("reagendar_cuota_factura", {
      p_tabla: cuota_tabla,
      p_cuota_id: cuota_id,
      p_nueva_fecha: nuevaFecha,
      p_pais: pais,
    }));
  } catch (err) {
    // Fase 2d (fisura f): `client.rpc` RECHAZANDO la promesa (excepción JS antes de un {data,error}
    // normal — red caída, timeout) nunca revienta este writer sin control (10.177) — se captura y se
    // devuelve con el MISMO contrato {error:string} del resto de esta función.
    return { error: err?.message || "Error de red al llamar reagendar_cuota_factura." };
  }
  if (error) {
    // Fase 2d (fisura f): un {error} SIN .message (o con .message vacío) NUNCA se trata como éxito ni
    // devuelve un mensaje vacío/confuso — nunca un falso éxito (10.177).
    let mensaje;
    if (typeof error.message === "string" && error.message.length > 0) {
      mensaje = error.message.startsWith(PREFIJO_ERROR_RPC)
        ? error.message.slice(PREFIJO_ERROR_RPC.length)
        : error.message;
    } else {
      mensaje = `Error sin mensaje de la RPC (code ${error.code ?? "desconocido"})`;
    }
    return { error: mensaje };
  }
  return { success: true, cuota_id, cuota_tabla, nuevaFecha, cuota: data };
};
